import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Mail, Lock, TrendingUp, User, Eye, EyeOff, ChevronDown, ChevronUp, CheckCircle, Tag, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { membershipApi, couponApi, enrollmentApi } from '../services/api';
import './LoginPage.css';

const LoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, isAuthenticated, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Redirect to dashboard if already logged in (or to redirect URL if provided)
  useEffect(() => {
    if (!loading && isAuthenticated) {
      const redirectUrl = searchParams.get('redirect');
      if (redirectUrl) {
        console.log('User already authenticated, redirecting to:', redirectUrl);
        navigate(redirectUrl);
      } else {
        console.log('User already authenticated, redirecting to dashboard');
        navigate('/dashboard');
      }
    }
  }, [loading, isAuthenticated, navigate, searchParams]);
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [pendingCouponRedirect, setPendingCouponRedirect] = useState(null);

  // Handle coupon from URL parameter (from /redeem page redirect)
  useEffect(() => {
    const couponFromUrl = searchParams.get('coupon');
    if (couponFromUrl) {
      setCouponCode(couponFromUrl.toUpperCase());
      setPendingCouponRedirect(couponFromUrl.toUpperCase());
      // Auto-validate the coupon
      validateCouponFromUrl(couponFromUrl.toUpperCase());
    }
  }, [searchParams]);

  const validateCouponFromUrl = async (code) => {
    setCouponLoading(true);
    setCouponError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/redeem/${code}`);
      const data = await response.json();
      
      if (response.ok && data.coupon) {
        // Find the membership this coupon applies to
        const product = data.products?.[0];
        if (product) {
          setAppliedCoupon({
            code: data.coupon.code,
            discount_type: data.coupon.discount_type,
            discount_value: data.coupon.discount_value,
            product: product
          });
          setSelectedPlan({
            id: product.id,
            name: product.name,
            price: product.price,
            period: product.period
          });
          setActiveTab('register'); // Switch to register for new users
        }
      }
    } catch (err) {
      console.error('Failed to validate coupon from URL:', err);
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.tab === 'register') {
      setActiveTab('register');
    }
    if (location.state?.selectedPlan) {
      setSelectedPlan(location.state.selectedPlan);
      setSelectedCourse(null);
      setAppliedCoupon(null);
    }
    if (location.state?.selectedCourse) {
      setSelectedCourse(location.state.selectedCourse);
      setSelectedPlan(null);
      setAppliedCoupon(null);
    }
  }, [location]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(true); // Default to true for better UX
  const { toast } = useToast();

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const [isLoading, setIsLoading] = useState(false);

  // Coupon validation handler
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    const product = selectedPlan || selectedCourse;
    if (!product) {
      setCouponError('Please select a product first');
      return;
    }

    setCouponLoading(true);
    setCouponError('');
    
    try {
      const result = await couponApi.validate(
        couponCode,
        product.id,
        selectedPlan ? 'membership' : 'course'
      );
      
      if (result.valid) {
        setAppliedCoupon({
          code: couponCode.toUpperCase(),
          ...result
        });
        setCouponCode('');
      } else {
        setCouponError(result.message);
        setAppliedCoupon(null);
      }
    } catch (error) {
      setCouponError('Failed to validate coupon');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      if (activeTab === 'login') {
        if (!formData.email || !formData.password) {
          toast({
            title: 'Error',
            description: 'Please fill in all fields',
            variant: 'destructive'
          });
          setIsLoading(false);
          return;
        }
        const result = await login(formData.email, formData.password, rememberMe);
        if (result.success) {
          toast({
            title: 'Success',
            description: 'Logged in successfully'
          });
          
          // Check if we have a redirect URL from query params (like from mentorship page)
          const redirectUrl = searchParams.get('redirect');
          if (redirectUrl) {
            console.log('Login success - redirecting to:', redirectUrl);
            navigate(redirectUrl);
            return;
          }
          
          // Check if we need to return to a specific page (like /claim/FREEYEAR)
          // Use window.location for claim pages to ensure proper redirect
          if (location.state?.returnTo) {
            const returnPath = location.state.returnTo;
            console.log('Login success - redirecting to:', returnPath);
            if (returnPath.startsWith('/claim/')) {
              window.location.href = returnPath;
            } else {
              navigate(returnPath);
            }
            return;
          }
          
          // If user came from coupon redemption, redirect to checkout
          if (pendingCouponRedirect && selectedPlan && selectedPlan.id) {
            setIsProcessing(true);
            try {
              const upgradeResponse = await membershipApi.upgradeMembership(
                selectedPlan.id,
                pendingCouponRedirect
              );
              if (upgradeResponse.checkout_url) {
                window.location.href = upgradeResponse.checkout_url;
                return;
              }
            } catch (upgradeError) {
              console.error('Failed to initiate coupon checkout:', upgradeError);
            }
          }
          
          // Check if onboarding is completed - route will handle redirect
          navigate('/dashboard');
        } else {
          toast({
            title: 'Error',
            description: result.error || 'Login failed',
            variant: 'destructive'
          });
        }
      } else {
        if (!formData.name || !formData.email || !formData.password) {
          toast({
            title: 'Error',
            description: 'Please fill in all fields',
            variant: 'destructive'
          });
          setIsLoading(false);
          return;
        }
        const result = await register(formData.name, formData.email, formData.password);
        if (result.success) {
          toast({
            title: 'Success',
            description: 'Account created successfully'
          });
          
          // Check if we have a redirect URL from query params (like from mentorship page)
          const redirectUrl = searchParams.get('redirect');
          if (redirectUrl) {
            console.log('Registration success - redirecting to:', redirectUrl);
            navigate(redirectUrl);
            return;
          }
          
          // Check if we need to return to a specific page (like /claim/FREEYEAR)
          // Use window.location for more reliable redirect after registration
          if (location.state?.returnTo) {
            console.log('Registration success - redirecting to:', location.state.returnTo);
            window.location.href = location.state.returnTo;
            return;
          }
          
          // Check if user selected a paid plan
          if (selectedPlan && selectedPlan.price > 0 && selectedPlan.id) {
            setIsProcessing(true);
            try {
              // Redirect to Stripe checkout for the selected plan
              // Pass coupon code if applied
              const upgradeResponse = await membershipApi.upgradeMembership(
                selectedPlan.id,
                appliedCoupon ? appliedCoupon.code : null
              );
              if (upgradeResponse.checkout_url) {
                window.location.href = upgradeResponse.checkout_url;
                return;
              }
            } catch (upgradeError) {
              console.error('Failed to initiate plan checkout:', upgradeError);
              // Still navigate to onboarding even if upgrade fails
            }
          }
          
          // Check if user selected a paid course (like HyperDrive Manual)
          if (selectedCourse && selectedCourse.price > 0 && selectedCourse.id) {
            setIsProcessing(true);
            try {
              // Redirect to Stripe checkout for the course
              // Pass coupon info if applied
              const enrollResponse = await enrollmentApi.enrollInCourse(
                selectedCourse.id,
                appliedCoupon ? appliedCoupon.code : null
              );
              if (enrollResponse.checkout_url) {
                window.location.href = enrollResponse.checkout_url;
                return;
              }
            } catch (enrollError) {
              console.error('Failed to initiate course checkout:', enrollError);
              toast({
                title: 'Checkout Error',
                description: enrollError.message || 'Failed to initiate checkout. Please try again.',
                variant: 'destructive'
              });
              // Still navigate to onboarding even if checkout fails
            }
          }
          
          // Navigate to onboarding for new users
          navigate('/onboarding');
        } else {
          toast({
            title: 'Error',
            description: result.error || 'Registration failed',
            variant: 'destructive'
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'An error occurred',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setFormData({ name: '', email: '', password: '' });
  };

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Logo and Title */}
        <div className="brand-section">
          <div className="logo-icon">
            <TrendingUp size={32} strokeWidth={2.5} />
          </div>
          <h1 className="brand-title">HyperDrive</h1>
          <p className="brand-subtitle">Advanced Trading Indicators Platform</p>
        </div>

        {/* Login/Register Card */}
        <div className="auth-card">
          <div className="card-header">
            <h2 className="card-title">Welcome Back</h2>
            <p className="card-subtitle">Sign in to access your trading indicators</p>
          </div>

          {/* Tabs */}
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'login' ? 'active' : ''}`}
              onClick={() => switchTab('login')}
            >
              Login
            </button>
            <button
              className={`tab ${activeTab === 'register' ? 'active' : ''}`}
              onClick={() => switchTab('register')}
            >
              Register
            </button>
          </div>

          {/* Selected Plan Dropdown */}
          {activeTab === 'register' && selectedPlan && (
            <div className={`selected-plan-dropdown ${selectedPlan.price === 0 ? 'free-plan' : ''}`}>
              <div 
                className="plan-header"
                onClick={() => setShowPlanDetails(!showPlanDetails)}
              >
                <div className="plan-header-left">
                  <span className="plan-label">Selected Plan:</span>
                  <span className="plan-name">{selectedPlan.name}</span>
                </div>
                <div className="plan-header-right">
                  <span className={`plan-price ${selectedPlan.price === 0 ? 'free' : ''}`}>
                    {selectedPlan.price === 0 ? 'FREE' : `$${selectedPlan.price}${selectedPlan.period === 'month' ? '/mo' : '/year'}`}
                  </span>
                  {showPlanDetails ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </div>
              
              {showPlanDetails && (
                <div className="plan-details">
                  {selectedPlan.description && (
                    <p className="plan-description">{selectedPlan.description}</p>
                  )}
                  {selectedPlan.features && selectedPlan.features.length > 0 && (
                    <ul className="plan-features-list">
                      {selectedPlan.features.map((feature, idx) => (
                        <li key={idx}>
                          <CheckCircle size={14} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  <button 
                    type="button" 
                    className="go-back-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/#pricing');
                    }}
                  >
                    ← Go Back to Plans
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Selected Course Dropdown */}
          {activeTab === 'register' && selectedCourse && (
            <div className="selected-plan-dropdown course-plan">
              <div 
                className="plan-header"
                onClick={() => setShowPlanDetails(!showPlanDetails)}
              >
                <div className="plan-header-left">
                  <span className="plan-label">Selected Course:</span>
                  <span className="plan-name">{selectedCourse.title}</span>
                </div>
                <div className="plan-header-right">
                  <span className="plan-price">
                    ${selectedCourse.price} <span className="one-time-label">One-Time</span>
                  </span>
                  {showPlanDetails ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </div>
              
              {showPlanDetails && (
                <div className="plan-details">
                  {selectedCourse.description && (
                    <p className="plan-description">{selectedCourse.description}</p>
                  )}
                  {selectedCourse.features && selectedCourse.features.length > 0 && (
                    <ul className="plan-features-list">
                      {selectedCourse.features.map((feature, idx) => (
                        <li key={idx}>
                          <CheckCircle size={14} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  {selectedCourse.title?.toLowerCase().includes('manual') && (
                    <div className="course-bonus-banner">
                      <div className="bonus-highlight">
                        <span className="bonus-icon">🎁</span>
                        <span className="bonus-main-text">Includes 30 Days of Live Trading FREE</span>
                      </div>
                      <p className="bonus-renewal-text">Then $99/mo for continued access. Cancel anytime.</p>
                    </div>
                  )}
                  <button 
                    type="button" 
                    className="go-back-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/#pricing');
                    }}
                  >
                    ← Go Back to Plans
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="auth-form">
            {activeTab === 'register' && (
              <div className="form-group">
                <label>Name</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}

            {/* Redeem Coupon Link - above email */}
            <div className="redeem-link-container top">
              <p>Have a special coupon? <a href="/redeem" className="redeem-link">Redeem here</a></p>
            </div>

            <div className="form-group">
              <label>Email</label>
              <div className="input-wrapper">
                <input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="input-wrapper password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button 
                  type="button"
                  className="show-password-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {activeTab === 'login' && (
              <div className="remember-me-wrapper">
                <label className="remember-me-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="remember-me-checkbox"
                  />
                  <span className="remember-me-text">Remember me for 30 days</span>
                </label>
              </div>
            )}

            {/* Coupon Code Section */}
            {activeTab === 'register' && (selectedPlan || selectedCourse) && (
              <div className="coupon-section">
                {!appliedCoupon ? (
                  <>
                    <div className="coupon-input-wrapper">
                      <Tag size={18} className="coupon-icon" />
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                        placeholder="Enter coupon code"
                        className="coupon-input"
                      />
                      <button 
                        type="button" 
                        onClick={handleApplyCoupon} 
                        className="apply-coupon-btn"
                        disabled={couponLoading}
                      >
                        {couponLoading ? <Loader2 size={16} className="spin" /> : 'Apply'}
                      </button>
                    </div>
                    {couponError && <p className="coupon-error">{couponError}</p>}
                  </>
                ) : (
                  <div className="coupon-applied">
                    <div className="coupon-applied-info">
                      <Tag size={16} />
                      <span className="coupon-applied-code">{appliedCoupon.code}</span>
                      <span className="coupon-applied-discount">
                        {appliedCoupon.discount_type === 'percentage' 
                          ? `${appliedCoupon.discount_value}% off` 
                          : `$${appliedCoupon.discount_value} off`}
                      </span>
                    </div>
                    <button type="button" onClick={handleRemoveCoupon} className="remove-coupon-btn">
                      Remove
                    </button>
                  </div>
                )}
                {appliedCoupon && (
                  <div className="price-breakdown">
                    <div className="price-row">
                      <span>Original Price:</span>
                      <span className="original-price">${appliedCoupon.original_price}</span>
                    </div>
                    <div className="price-row discount">
                      <span>Discount:</span>
                      <span>-${(appliedCoupon.original_price - appliedCoupon.discounted_price).toFixed(2)}</span>
                    </div>
                    <div className="price-row total">
                      <span>Total:</span>
                      <span className="final-price">${appliedCoupon.discounted_price}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button type="submit" className="submit-btn" disabled={isLoading || isProcessing}>
              {isLoading ? 'Please wait...' : 
               isProcessing ? 'Redirecting to checkout...' :
               (activeTab === 'login' ? 'Sign In' : 
                ((selectedPlan && selectedPlan.price > 0) || (selectedCourse && selectedCourse.price > 0) 
                  ? `Create Account & Checkout${appliedCoupon ? ` ($${appliedCoupon.discounted_price})` : ''}`
                  : 'Create Account'))}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="footer">
          <p>© 2025 HyperDrive. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
